import { Component } from 'react';
import { connect } from 'react-redux';
import { graphql } from '@apollo/client/react/hoc';
import { compose, withHandlers, withState } from 'react-recompose';
import { DropTarget } from 'react-dnd';
import _, { constant, flatten, get, isEmpty, last, map } from 'lodash';
import { client } from '../../common/ApolloClient';
import { CreateVizOptions, DiscoverQueries } from '../../common/graphql/index';
import { updateVisualizationOp } from '../../common/graphql/DiscoverQueries';
import { withSkeletonLoadingPanel } from '../../common/hoc/index';
import Discover from '../../common/redux/actions/DiscoverActions';
import {
  DnDTypes,
  VIZ,
  Types,
  setDynamicValues,
  USE_FISCAL_REPORTING,
  VIZ_OPTION_IDS,
} from '../../common/Constants';
import {
  getConfigPanelDetail,
  VIZ_SELECTORS,
} from '../../common/redux/selectors/viz-selectors';
import { MobileVizFooter } from '../../components/MobileVizFooter';
import VizSaveDialog from '../../views/VizSaveDialog';
import { useGetChartComponent } from '../viz-chart/viz-chart.hook';
import { DiscoveryToolbar } from '../discovery-toolbar';
import VizConfigPanel from '../VizConfigPanel';
import { ILegendGroupDatum, VizLegend } from '../charts/viz-legend';
import { ActiveFilterPanel } from '../filter/active-filter-panel';
import { FilterDialog } from '../filter/filter-dialog/filter-dialog.component';
import RemoveFieldFromVizDialog from '../RemoveFieldFromVizDialog';
import { Viz, Viz as VizUtil } from '../VizUtil';
import { QueryErrorDialog } from '../query-error';
import AddFieldToVizWarningDialog from '../AddFieldToVizWarningDialog';
import VizChartError from '../VizChartError';
import QueryWarningDialog from '../QueryWarningDialog';
import { messages } from '../../i18n';
import { SlicerPanel } from '../slicer';
import { VizPanel } from '../viz-panel';
import { UpdateVizOptions } from '../../common/graphql/util';
import { withDiscoverRouter } from '../../common/utilities/with-router';
import { withDiscoverOption } from '../discovery-context/discovery.context';
import {
  IDispatchToProps,
  IProps,
  IState,
  IStateToProps,
} from './viz.interfaces';
import { FilterDialogContextProvider } from '../filter/filter-dialog/filter-dialog.context';
import { IDiscoveryTracked, IRevision, ISaveViz, IViz } from '../interfaces';
import { SaveVersionDialog } from '../../views/save-version-dialog';
import { ErrorBoundary } from 'react-error-boundary';
import { LogErrorBoundary } from '../../components/error-boundary';
import { ADD_VISUALIZATION_QUERY_NAME } from '../../common/graphql/Fragments';
import ColorManager from '../../common/d3/ColorManager';
import { IAnyAttribute } from '../../datasets/interfaces/attribute.interface';
import { LineChartUtils } from '../charts/LineChartUtils/LineChartUtils';
import { parseJSON } from '../../common/Util';
import { withSugarCoreDropdownColors } from '../../common/hoc/SugarCoreDropdownColors';
import { VizChart } from '../viz-chart';
import { LegacyVizChart } from '../viz-chart/viz-chart.memo';

export class UnconnectedVizComponent extends Component<IProps, IState> {
  props;
  state;

  constructor(props) {
    super(props);
  }

  needsFiscalCalendarUpdate(currentFiscalCalendar, locationFiscalCalendar) {
    return (
      !_.isNil(locationFiscalCalendar) &&
      currentFiscalCalendar !== locationFiscalCalendar
    );
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.dropTargetId &&
      this.props.dropTargetId !== prevProps.dropTargetId
    ) {
      /*
       keep the app state in sync with what viz trash drop target is active
       so we can rely on it in the FieldPillDragPreview to find out if it is currently over it
      */
      prevProps.setVizTrashDropTarget(this.props.dropTargetId);
    }
    // Perform dirty checks
    if (!_.isEqual(prevProps.rawDiscovery, this.props.rawDiscovery)) {
      const { rawDiscovery, discovery } = this.props;
      const { discovery: prevDiscovery } = prevProps;
      let dirty = false;

      // @NOTE dirty should be a calculated value and not set by components. saveCheckpoint is not currently tracked by redux-undo
      if (!_.isNil(rawDiscovery?.saveCheckpoint)) {
        dirty =
          (rawDiscovery?.saveCheckpoint ?? 0) !==
          (rawDiscovery?.past ?? [])?.length;
      } else {
        dirty = rawDiscovery?.past?.length > 0;
      }
      if (dirty && !rawDiscovery?.present?.dirty) {
        // Update present discovery with captured dirty state
        this.props.setVizDirty(discovery?.viz?.id, dirty);
      }
      if (VizUtil.shouldClearQuery(discovery, prevDiscovery)) {
        this.props.clearQueryResults(discovery?.viz?.id);
      }
    }
  }

  componentDidMount() {
    // Get location state from react router
    const { state }: { state: any } = this.props.location;
    if (!_.isEmpty(state?.dynamicValues)) {
      setDynamicValues(state?.dynamicValues);
    }

    if (
      state &&
      (!_.isNil(state.filters) ||
        !_.isNil(state.removeFiltersOnFields) ||
        !_.isNil(state.calcs) ||
        !_.isNil(state.metrics) ||
        !_.isNil(state.toShelves) ||
        !_.isNil(state.slicers))
    ) {
      // Apply any selected viz state
      this.props.applyVizState({
        filters: state.filters,
        prependFilters: true,
        removeFiltersOnFields: state.removeFiltersOnFields,
        calcs: state.calcs,
        metrics: state.metrics,
        toShelves: state.toShelves,
        slicers: state.slicers,
        slicerSelections: state.slicerSelections,
      });
    } else {
      this.props.setRouterApplyingState(false);
    }
    if (
      this.needsFiscalCalendarUpdate(
        this.props.useFiscalCalendar,
        state?.useFiscalCalendar,
      )
    ) {
      this.props.setUsingFiscalCalendar(
        state?.useFiscalCalendar,
        this.props?.discovery?.viz?.id,
      );
    }
  }

  onSaveVersion() {
    this.props.setShowVersionDialog(true);
  }

  onSaveIconClick(saveAs = false) {
    this.props.setSaveActive(true);
    if (saveAs) {
      this.props.setShowSaveAsDialog(true);
    } else if (Viz.isNewViz(this.props.viz.id)) {
      this.props.setShowSaveDialog(true);
    } else {
      this.props.update();
    }
  }

  render() {
    const saveAsProps = {
      showSaveDialog: this.props.showSaveAsDialog,
      discovery: { ...this.props.discovery, dirty: false },
      viz: { ...this.props.viz },
      saveActive: this.props.saveActive,
      saveError: this.props.saveError,
      setSaveActive: this.props.setSaveActive,
      setShowSaveDialog: this.props.setShowSaveAsDialog,
      saveNew: ({ name: newName, optionalViz }) =>
        this.props.saveAs(newName, optionalViz),
      update: _.noop,
      changeDiscoveryName: _.noop,
      changeDiscoveryTags: _.noop,
      closeViz: _.noop,
      cancelDiscoverySave: () => this.props.cancelDiscoverySaveAs(),
    };

    // VizSaveDialog should eventually use ModalPortal
    const saveDialogProps = _.pick(this.props, [
      'saveNew',
      'changeDiscoveryName',
      'closeViz',
      'saveError',
      'discovery',
      'viz',
      'saveActive',
      'cancelDiscoverySave',
      'changeDiscoveryName',
      'isReadOnlyUser',
      'clearSaveError',
      'update',
    ]);

    const showSaveDialog =
      this.props.showSaveDialog || this.props.showSaveAsDialog;

    const { showVersionDialog } = this.props;

    const ChartComponent = this.props.getChartComponent();

    return this.props.connectDropTarget(
      <div className={'viz-container'}>
        <DiscoveryToolbar
          onSave={() => {
            this.onSaveIconClick();
          }}
          onSaveVersion={() => this.onSaveVersion()}
          onSaveAs={() => this.onSaveIconClick(true)}
          exportEnabled={
            !this.props.hasVizQueryError && this.props.hasVizQueryResults
          }
        />
        <div className={'viz-detail'}>
          <div
            className={`left-panel ${
              _.isEmpty(this.props.configPanelDetail) ? 'exit' : 'enter'
            }`}
          >
            <VizConfigPanel
              vizId={this.props.vizId}
              key={`vizConfigPanel-${this.props.vizId}`}
            />
          </div>

          {this.props.isMobile && <VizLegend vizId={this.props.vizId} />}
          {this.props.isMobile && this.props.showFiltersPanel && (
            <ActiveFilterPanel
              discoveryId={this.props.vizId}
              viz={this.props.viz}
              useFiscalCalendar={this.props.useFiscalCalendar}
            />
          )}

          <div className={'content'} style={{ overflowX: 'clip' }}>
            <div
              className={`viz-chart-top-panel ${
                _.isEmpty(this.props.configPanelDetail)
                  ? 'details-closed'
                  : 'details-open'
              }`}
            >
              {!this.props.isMobile && <SlicerPanel key={'slicer-panel'} />}
              {this.props.showFilterDialog && (
                <FilterDialogContextProvider
                  discoveryId={this.props.discovery?.viz?.id}
                >
                  <FilterDialog vizId={this.props.discovery?.viz?.id} />
                </FilterDialogContextProvider>
              )}
            </div>
            <div className={'viz-chart'}>
              {!this.props.routerApplyingState && (
                <ErrorBoundary
                  key={`vizErrorBoundary-${this.props.vizId}`}
                  fallbackRender={VizChartError}
                  onError={LogErrorBoundary}
                >
                  <ChartComponent
                    key={`vizChart-${this.props.vizId}`}
                    vizId={this.props.vizId}
                  />
                </ErrorBoundary>
              )}
            </div>
          </div>

          {!this.props.isMobile && (
            <VizPanel
              vizId={this.props.vizId}
              isMobile={this.props.isMobile}
              initialPanelWidth={_.get(
                this.props,
                'viz.options.legendPanelWidth',
                VIZ.LEGEND_PANEL_WIDTH,
              )}
              useFiscalCalendar={this.props.useFiscalCalendar}
            />
          )}

          {this.props.isMobile && <MobileVizFooter vizId={this.props.vizId} />}
        </div>

        {showSaveDialog && (
          <VizSaveDialog
            key={`save-dialog-${this.props.vizId}`}
            {...(this.props.showSaveDialog ? saveDialogProps : saveAsProps)}
            isSaveAs={!!this.props.showSaveAsDialog}
          />
        )}

        {showVersionDialog && (
          <SaveVersionDialog
            reportName={this.props.discovery?.viz?.name}
            vizId={this.props.vizId}
            onSave={this.props.update}
            onCancel={() => this.props.setShowVersionDialog(false)}
          />
        )}

        <RemoveFieldFromVizDialog />
        <AddFieldToVizWarningDialog />
        <QueryWarningDialog vizId={this.props.vizId} />
        <QueryErrorDialog vizId={this.props.vizId} />
      </div>,
    );
  }
}

const mapStateToProps = (state, props): IStateToProps => {
  const openDiscovery: IDiscoveryTracked =
    state.discover.openDiscoveries[props.vizId];
  if (!openDiscovery) {
    return {
      error: messages.viz.discoveryNotFound,
    };
  }
  let discovery = openDiscovery.present;
  let missingFieldsInfo = {} as any;

  if (!_.isNil(discovery?.viz)) {
    missingFieldsInfo = VizUtil.findMissingFields(discovery?.viz);
    // favor missing field info that comes in on the viz
    const missingFields = !_.isEmpty(discovery?.missingFields)
      ? discovery?.missingFields
      : missingFieldsInfo.missingFields;
    const missingDependentFields = !_.isEmpty(discovery?.missingDependentFields)
      ? discovery?.missingDependentFields
      : missingFieldsInfo.missingDependentFields;
    const goodFields = !_.isEmpty(discovery?.goodFields)
      ? discovery?.goodFields
      : missingFieldsInfo.goodFields;
    const missingFilters = !_.isEmpty(discovery?.missingFilters)
      ? discovery?.missingFilters
      : missingFieldsInfo.missingFilters;

    discovery = {
      ...discovery,
      missingFields,
      missingDependentFields,
      goodFields,
      missingFilters,
    };
  } else {
    discovery.viz = {} as IViz;
  }

  const useFiscalCalendar =
    VIZ_SELECTORS.hasVizDatasetFiscalCalendarSetting(state, props) &&
    VIZ_SELECTORS.getActiveVizFiscalSetting(state, props) === 'true';

  return {
    rawDiscovery: openDiscovery,
    discovery,
    configPanelDetail: getConfigPanelDetail(state, {
      discoveryId: discovery?.viz?.id,
    }),
    viz: discovery?.viz,
    saveActive: state.discover.saveActive,
    saveError: state.discover.saveError,
    showSaveDialog: state.discover.showSaveDialog,
    showVersionDialog: state.discover.showVersionDialog,
    showSaveAsDialog: state.discover.showSaveAsDialog,
    showFilterDialog: state.discover.showFieldFilterDialog,
    isMobile: state.main.isMobile,
    interactivePanelDetail: _.get(
      discovery,
      `viz.options.${VIZ_OPTION_IDS.panelDetail}`,
      null,
    ),
    hasVizQueryError: !!discovery.vizQueryError,
    hasVizQueryResults: !!discovery.vizQueryResults,
    useFiscalCalendar,
  };
};

const mapDispatchToProps = (dispatch, ownProps): IDispatchToProps => {
  return {
    setSaveActive: value => {
      dispatch(Discover.setSaveActive(value));
    },
    setSaveError: error => {
      dispatch(Discover.setSaveError(error));
    },
    clearSaveError: () => {
      dispatch(Discover.setSaveError(undefined));
    },
    setDisplayDiscovery: id => {
      dispatch(Discover.setDisplayDiscovery(id));
    },
    setShowSaveDialog: value => {
      dispatch(Discover.setShowSaveDialog(value));
    },
    setShowVersionDialog: value => {
      dispatch(Discover.setShowVersionDialog(value));
    },
    setShowSaveAsDialog: value => {
      dispatch(Discover.setShowSaveAsDialog(value));
    },
    setVizDirty: (id, dirty) => {
      dispatch(Discover.setDiscoveryDirty(id, dirty));
    },
    setSaveCheckpoint: (id, updatedOn, revisions) => {
      dispatch(Discover.setDiscoverySaveCheckpoint(id, updatedOn, revisions));
      dispatch(Discover.setDiscoveryDirty(id, false));
    },
    closeVizDialog: () => {
      dispatch(Discover.setShowSaveDialog(false));
      dispatch(Discover.setShowSaveAsDialog(false));
      dispatch(Discover.setShowVersionDialog(false));
      dispatch(Discover.setSaveActive(false));
    },
    setVizIdAfterSave: (id, creatorName, creator) => {
      dispatch(
        Discover.updateVizWithId(ownProps.vizId, id, creatorName, creator),
      );
    },
    clearQueryResults: id => {
      dispatch(Discover.clearQueryResults(id));
    },
    closeViz: id => {
      dispatch(Discover.setDiscoveryDirty(id, false));
      dispatch(Discover.setShowSaveDialog(false));
      const force = true;
      dispatch(Discover.closeDiscovery(id), { force });
    },
    _removeField: (id, field, shelf, layout) => {
      const priorPeriodRefs = VizUtil.fieldIsReferencedByPriorPeriodCalc(
        layout,
        field,
      );
      if (
        field.attributeType === Types.PRIOR_PERIOD_CALC ||
        !_.isEmpty(priorPeriodRefs)
      ) {
        // need to prompt the user first
        dispatch(Discover.showConfirmRemoveField(id, field, shelf));
      } else {
        dispatch(Discover.removeFieldFromVisualization(id, field, shelf));
      }
    },
    setVizTrashDropTarget: targetId => {
      dispatch(Discover.setVizTrashDropTarget(targetId));
    },
    changeDiscoveryName: (id, name) => {
      dispatch(Discover.changeDiscoveryName(id, name));
    },
    changeDiscoveryTags: (id, tags) => {
      dispatch(Discover.setTagsForViz(id, tags));
    },
    copyVizState: (fromId, toId, toName) => {
      dispatch(Discover.copyVizState(fromId, toId, toName));
    },
    copyUndoStack: (fromId, toId, toName) => {
      dispatch(Discover.copyUndoStack(fromId, toId, toName));
    },
    applyVizState: state => {
      dispatch(Discover.applyVizState({ id: ownProps.vizId, ...state }));
      ownProps.setRouterApplyingState(false);
    },
    setPanelDetail: panel => {
      dispatch(Discover.setPanelDetail(ownProps.vizId, panel));
    },
    cancelDiscoverySave: () => {
      dispatch(Discover.setSaveError(undefined));
      dispatch(Discover.cancelDiscoverySave());
    },
    cancelDiscoverySaveAs: () => {
      dispatch(Discover.cancelDiscoverySaveAs());
    },
    setUsingFiscalCalendar: (useFiscalCalendar, id?) => {
      if (_.isNil(id)) {
        return;
      }

      dispatch(
        Discover.setSettingForViz({
          id,
          setting: USE_FISCAL_REPORTING,
          value: useFiscalCalendar,
        }),
      );
    },
    updateVizSetting: (id, key, value) => {
      dispatch(Discover.updateVizSetting(id, key, value));
    },
  };
};
const handlers = {
  updateVizForSave:
    props =>
    (optionalViz): ISaveViz => {
      const { isPrivate } = props.viz;
      let viz = _.isNil(optionalViz) ? props.viz : optionalViz;
      const groupName = viz?.id ?? props.discovery?.viz?.id;
      let customColors: Record<string, any> & {
        colorGroup?: Record<string, string>;
      } = {};
      const group = ColorManager.getCustomGroup(groupName);
      if (props.discovery?.legendData) {
        customColors = props.discovery.legendData.reduce((acc, prev) => {
          if (prev.label) {
            let colorKey = prev.label;
            if (viz?.chartType === 'line') {
              colorKey = LineChartUtils.getColorKey({
                lines: [prev.label],
                valueName: (last(viz.layout.VALUES) as IAnyAttribute)?.name,
              });
            }
            if (group?.[colorKey]) {
              acc[colorKey] = group[colorKey];
            }
          }
          return acc;
        }, {});
      }
      const legendGroupData: ILegendGroupDatum[] = Viz.getOptionFromViz({
        viz,
        option: VIZ_OPTION_IDS.legendGroupMetadata,
        defaultValue: [],
      });
      if (!isEmpty(legendGroupData)) {
        const legendGroupColorKeys = [
          ...map(legendGroupData, 'id'),
          ...flatten(map(legendGroupData, 'fields')),
        ];
        customColors.colorGroup = legendGroupColorKeys.reduce(
          (acc, colorKey) => {
            if (group?.[colorKey]) {
              acc[colorKey] = group[colorKey];
            }
            return acc;
          },
          {},
        );
      }
      viz.options[VIZ_OPTION_IDS.customColors] = JSON.stringify({
        ...customColors,
        ...parseJSON(viz.options[VIZ_OPTION_IDS.customColors]),
      });
      viz = VizUtil.updateVizForSave(
        viz,
        props.discovery?.viz?.id,
        props.discovery?.viz?.name,
        props.discovery?.viz?.tags,
      );
      return { ...viz, private: isPrivate };
    },
  // here we have access to the props set in mapStateToProps where we don't in mapDispatchToProps
  removeField: props => (id, field, shelf, layout) => {
    props._removeField(id, field, shelf, layout, props.viz.chartType);
  },
  updateColorManagerKey: props => (newId: string) => {
    ColorManager.updateGroupKey(props.vizId, newId);
  },
};

const handleSaveError = (error, ownProps) => {
  ownProps.setSaveError(error.message);
  if (!ownProps.showSaveDialog && !ownProps.showSaveAsDialog) {
    ownProps.setSaveActive(true);
    ownProps.setShowSaveDialog(true);
  }
};

const SaveVizMutation = graphql(DiscoverQueries.CreateVisualizationMutation, {
  options: CreateVizOptions,
  props: ({
    ownProps,
    mutate,
  }: {
    ownProps: Partial<IProps>;
    mutate: any;
  }): any => ({
    saveNew({ name, optionalViz, closeAfterSave = false }) {
      ownProps.clearSaveError();
      const viz = ownProps.updateVizForSave(optionalViz);
      viz.name = name;
      mutate({
        variables: {
          viz,
        },
      })
        .then(
          ({
            data,
          }: {
            data: {
              addVisualization: Partial<IViz>;
            };
          }) => {
            if (closeAfterSave) {
              ownProps.closeViz(ownProps.discovery?.viz?.id);
              return;
            }

            // viz save dialog can update description
            ownProps.updateVizSetting(
              ownProps.discovery?.viz?.id,
              VIZ_OPTION_IDS.description,
              optionalViz?.options?.[VIZ_OPTION_IDS.description],
            );

            // Visualization successfully saved, remove dirty flag and close dialog if open
            ownProps.setSaveCheckpoint(
              ownProps.discovery?.viz?.id,
              data[ADD_VISUALIZATION_QUERY_NAME].updatedOn,
              data[ADD_VISUALIZATION_QUERY_NAME].revisions,
            );

            ownProps.setVizIdAfterSave(
              data[ADD_VISUALIZATION_QUERY_NAME].id,
              data[ADD_VISUALIZATION_QUERY_NAME].creatorName,
              data[ADD_VISUALIZATION_QUERY_NAME].creator,
            );
            ownProps.updateColorManagerKey(
              data[ADD_VISUALIZATION_QUERY_NAME].id,
            );
            ownProps.setDisplayDiscovery(data[ADD_VISUALIZATION_QUERY_NAME].id);
            ownProps.closeVizDialog();
          },
        )
        .catch(error => {
          handleSaveError(error, ownProps);
        });
    },
    saveAs(newName, optionalViz) {
      ownProps.clearSaveError();
      const apolloClient = client;
      const viz = ownProps.updateVizForSave(optionalViz);
      const copiedFromId = viz.id;
      viz.name = newName;
      viz.id = undefined;
      mutate({
        variables: {
          viz,
        },
      })
        .then(({ data }) => {
          const savedId = get(data, `${ADD_VISUALIZATION_QUERY_NAME}.id`);
          const savedName = newName;

          // Force the reload of the discovery query. the renderOpen method that handle the '/open' route needs that cache to be fresh.
          // However, The update option of the mutation runs before the refetchQueries finish. So, we have to resort to this...
          apolloClient
            .query({
              query: DiscoverQueries.DiscoveriesQuery,
              fetchPolicy: 'network-only',
            })
            .then(() => {
              ownProps.setDisplayDiscovery(savedId);
              ownProps.closeVizDialog();

              // to make sure the newly created discovery is opened before we copy the state over, we pop that request out of the current execution stack
              setTimeout(() => {
                ownProps.copyVizState(copiedFromId, savedId, savedName);
              }, 1);

              // To preserve the undo/redo stack, the viz has to load as normal (wiping forcing undo/redo empty) before we try to copy over the undo/redo.
              // done too soon and it will get wiped out.
              setTimeout(() => {
                ownProps.copyUndoStack(copiedFromId, savedId, savedName);
              }, 300);

              // NOTE: we copy the view state of the viz first, with minimal wait to avoid screen flicker, but that is too soon for the undo/redo
            })
            .catch(() => {
              console.warn(`Save as failed for ${newName}`);
            });
        })
        .catch(error => {
          handleSaveError(error, ownProps);
        });
    },
  }),
});

const UpdateVizMutation = graphql(DiscoverQueries.UpdateVisualizationMutation, {
  options: UpdateVizOptions,
  props: ({
    ownProps,
    mutate,
  }: {
    ownProps: Partial<IProps>;
    mutate: any;
  }): any => ({
    update({
      optionalViz,
      closeAfterSave,
    }: { optionalViz?: IViz; closeAfterSave?: boolean } = {}) {
      ownProps.clearSaveError();
      const viz = ownProps.updateVizForSave(optionalViz);
      if (!_.isEmpty(optionalViz?.name)) {
        viz.name = optionalViz.name;
      }
      return mutate({
        variables: {
          viz,
        },
      })
        .then(
          ({
            data,
          }: {
            data: {
              updateVisualization: {
                updatedOn: string;
                revisions: IRevision[];
              };
            };
          }) => {
            if (closeAfterSave) {
              ownProps.closeViz(ownProps.discovery?.viz?.id);
              return;
            }

            // Visualization successfully saved, remove dirty flag and close dialog if open
            ownProps.setSaveCheckpoint(
              ownProps.discovery?.viz?.id,
              data[updateVisualizationOp].updatedOn,
              data[updateVisualizationOp].revisions,
            );
            ownProps.closeVizDialog();
          },
        )
        .catch(error => {
          handleSaveError(error, ownProps);
        });
    },
  }),
});

const trashTarget = {
  drop(props, monitor) {
    // don't perform the drop if an inner drop zone already handled it
    if (monitor.didDrop()) {
      return;
    }

    const { field } = monitor.getItem();
    const { shelf } = monitor.getItem();
    if (!_.isNil(field) && !_.isNil(shelf)) {
      props.removeField(
        props.discovery?.viz?.id,
        field,
        shelf.id,
        props.viz.layout,
      );
    }
  },
};

export const GetChartComponentHoc = HOCComponent => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ChartComponent = useGetChartComponent(
    props?.vizId,
    VizChart,
    LegacyVizChart,
  );

  return (
    <HOCComponent {...props} getChartComponent={constant(ChartComponent)} />
  );
};

const trashCollect = (connectComponent: { dropTarget: any }, monitor) => {
  return {
    connectDropTarget: connectComponent.dropTarget(),
    isOver: monitor.isOver(),
    isOverCurrent: monitor.isOver({ shallow: true }),
    canDrop: monitor.canDrop(),
    dropTargetId: monitor.targetId,
  };
};

export const VizComponent = compose<any, any>(
  GetChartComponentHoc,
  withDiscoverRouter,
  withDiscoverOption({ option: VIZ_OPTION_IDS.showFiltersPanel }),
  withState('routerApplyingState', 'setRouterApplyingState', true),
  (connect as any)(mapStateToProps, mapDispatchToProps),
  (withHandlers as any)(handlers),
  withSugarCoreDropdownColors,
  withSkeletonLoadingPanel(),
  SaveVizMutation,
  UpdateVizMutation,
  DropTarget(DnDTypes.VIZ_FIELD, trashTarget, trashCollect),
)(UnconnectedVizComponent as any);
